import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { useStore } from '../../hooks/useStore';
import Container from '../../components/layout/Container';
import Skeleton from '../../components/common/Skeleton';
import EmptyState from '../../components/common/EmptyState';
import ProductCarousel from '../../components/catalog/ProductCarousel';
import CategoryTiles from '../../components/catalog/CategoryTiles';

// Home do storefront whitelabel. Layout estilo e-commerce (Nuvemshop /
// lojastartmodas): banner hero compacto + carrosséis de produtos +
// categorias. Selos de confiança removidos pra deixar o foco inicial nos
// produtos.

const SupplierHomePage = () => {
  const { store, loading: storeLoading } = useStore();
  const [bestSelling, setBestSelling] = useState([]);
  const [newest, setNewest] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (storeLoading || !store) return;
      setLoading(true);
      try {
        const [best, news, feat, cats] = await Promise.all([
          productService.getAll({ sort: 'bestSelling', limit: 8 }).catch(() => null),
          productService.getAll({ sort: 'newest', limit: 8 }).catch(() => null),
          productService.getAll({ featured: 'true', limit: 8 }).catch(() => null),
          categoryService.list().catch(() => null),
        ]);
        if (cancelled) return;
        setBestSelling(best?.rows || []);
        setNewest(news?.rows || []);
        setFeatured(feat?.rows || []);
        setCategories(Array.isArray(cats) ? cats : cats?.rows || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [store, storeLoading]);

  if (storeLoading) {
    return (
      <Container className="py-8">
        <Skeleton variant="block" height={280} />
      </Container>
    );
  }

  if (!store) {
    return (
      <Container className="py-8">
        <EmptyState title="Loja não encontrada" description="O link pode ter sido alterado ou removido." />
      </Container>
    );
  }

  return (
    <div>
      {/* Hero compacto — banner com gradiente do tema. Sem stats gigantes. */}
      <section className="bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 py-6 md:py-10">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-2">
              {store.name}
            </h1>
            {store.description && (
              <p className="text-sm md:text-base text-white/90 mb-4 max-w-xl line-clamp-2">
                {store.description}
              </p>
            )}
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-primary font-semibold rounded-md shadow hover:bg-white/95 transition-colors text-sm"
            >
              Ver catálogo <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <Container>
        <ProductCarousel
          title="Mais vendidos"
          description="Os queridinhos da loja"
          products={bestSelling}
          loading={loading}
          seeAllHref="/catalogo?sort=bestSelling"
          emptyHint="Em breve, produtos aqui."
        />

        <CategoryTiles categories={categories} />

        <ProductCarousel
          title="Novidades"
          description="Recém-chegados ao catálogo"
          products={newest}
          loading={loading}
          seeAllHref="/catalogo?sort=newest"
          emptyHint="Aguarde novidades em breve."
        />

        <ProductCarousel
          title="Em destaque"
          description="Selecionados pela loja"
          products={featured}
          loading={loading}
          seeAllHref="/catalogo?onSale=true"
        />

        {/* Sobre a loja */}
        {(store.description || store.contactEmail || store.contactPhone) && (
          <section className="my-12 bg-app-surface rounded-xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-app-text mb-2">
                  Sobre {store.name}
                </h2>
                {store.description && (
                  <p className="text-sm text-app-text-secondary leading-relaxed">
                    {store.description}
                  </p>
                )}
              </div>
              <div className="text-sm text-app-text-secondary space-y-1">
                {store.contactEmail && <p>Email: <strong className="text-app-text">{store.contactEmail}</strong></p>}
                {store.contactPhone && <p>Telefone: <strong className="text-app-text">{store.contactPhone}</strong></p>}
                {store.contactWebsite && (
                  <p>Site: <a href={store.contactWebsite} className="text-primary hover:underline" target="_blank" rel="noreferrer">{store.contactWebsite}</a></p>
                )}
              </div>
            </div>
          </section>
        )}
      </Container>
    </div>
  );
};

export default SupplierHomePage;
